// form
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import { Autocomplete, AutocompleteProps, TextField, createFilterOptions } from '@mui/material';

// ----------------------------------------------------------------------

interface Props<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  name: string;
  label?: string;
  helperText?: React.ReactNode;
}

const filter = createFilterOptions<any>();

export default function RHFTags<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined = true
>({
  name,
  label,
  helperText,
  ...other
}: Omit<Props<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'>) {
  const { control, setValue, getValues } = useFormContext();

  const handleChange = (event: any, newValue: any) => {
    // Split string into tags by space
    let tags = typeof newValue === 'string' ? newValue.split(' ') : newValue;

    // Remove empty strings caused by leading/trailing/multiple spaces
    tags = tags.filter((tag: any) => tag !== '');

    setValue(name, [
      ...tags.filter((tag: any) => typeof tag === 'string'),
      ...tags.filter((tag: any) => typeof tag === 'object' && tag !== null),
    ]);
  };

  const handleBlur = (event: any) => {
    const inputValue = event.target.value.trim();
    const value = getValues(name);
    if (inputValue !== '' && value.indexOf(inputValue) === -1) {
      const tags = inputValue.split(' ');
      setValue(name, [...value, ...tags]);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          selectOnFocus
          clearOnBlur
          open={false}
          handleHomeEndKeys
          onChange={handleChange}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            if (params.inputValue !== '') {
              filtered.push(params.inputValue);
            }
            return filtered;
          }}
          onBlur={handleBlur}
          renderInput={(params) => (
            <TextField
              label={label}
              error={!!error}
              helperText={error ? error?.message : helperText}
              {...params}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          )}
          {...other}
        />
      )}
    />
  );
}